import { Button, Tooltip } from "@ondit/ui";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight } from "lucide-react";
import { createContext, type ReactNode, useContext, useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router";
import { useAccess } from "~/core/access/index";
import { api } from "~/core/api/index";
import { useI18n, useT } from "~/core/i18n";
import { brand } from "~/core/paths";
import { keys, read } from "~/core/query";
import type { FilterState } from "~/modules/analytics/components/filters";
import { CONTENT_ICON, READING_ICONS } from "~/modules/analytics/components/readings";
import { StageFlyout } from "~/modules/analytics/components/stage-flyout";
import { ADVANCED_READINGS, CONTENT_PATH, LOOP } from "~/modules/analytics/lib/loop";
import { useSidebarChrome } from "~/shell/chrome";

/**
 * Whether the navigation is a rail.
 *
 * A context rather than a prop because every row needs the answer and none of
 * them needs anything else from the sidebar.
 */
const Rail = createContext(false);

/**
 * The label a row loses when the navigation becomes a rail, given back on
 * hover. Renders its child untouched while the labels are still on the page.
 */
function Hint({ label, side = "right", children }: { label: string; side?: "right" | "top"; children: ReactNode }) {
	const rail = useContext(Rail);
	if (!rail) return <>{children}</>;
	return (
		<Tooltip.Root>
			<Tooltip.Trigger asChild>{children}</Tooltip.Trigger>
			<Tooltip.Content side={side}>{label}</Tooltip.Content>
		</Tooltip.Root>
	);
}

function Item({
	to,
	label,
	end,
	badge,
	icon,
}: {
	to: string;
	label: string;
	end?: boolean;
	badge?: string;
	icon: ReactNode;
}) {
	return (
		// The badge joins the label in the tooltip: a rail has no room for a
		// number beside an icon, and the credit balance is the reason somebody
		// looks at that row.
		<Hint label={badge ? `${label} · ${badge}` : label}>
			{/* A plain string, not the callback form: in a rail the row is the
			    child of a tooltip trigger, and the clone that trigger makes
			    stringifies a className *function* into the class attribute —
			    which leaves the row with no `nav-item` class at all. */}
			<NavLink to={to} end={end} aria-label={label} className="nav-item">
				{icon}
				<span className="nav-label">{label}</span>
				{badge ? <span className="badge num">{badge}</span> : null}
			</NavLink>
		</Hint>
	);
}

/**
 * One stage of the loop, numbered, and open or folded away.
 *
 * The number is the point. Every screen in this module belongs to one of five
 * steps — measure, read the measurement, write the answer, publish it, measure
 * again — and a customer who cannot say which step they are on cannot say what
 * to do next.
 *
 * Sixteen rows and six headings is about 990px of column, which is taller than
 * the laptop it is being read on. Folding all but the stage in hand is what
 * keeps the five numbers on the screen at once — a diagram somebody has to
 * scroll to finish reading has stopped being a diagram.
 */
function Stage({
	step,
	icon,
	title,
	open = true,
	current = false,
	onToggle,
	children,
}: {
	step: number;
	icon: ReactNode;
	title: string;
	open?: boolean;
	/** This stage owns the screen being looked at. */
	current?: boolean;
	/** Absent in a rail and on a phone, where nothing folds. */
	onToggle?: () => void;
	children: ReactNode;
}) {
	const t = useT();
	const rail = useContext(Rail);
	// The heading carries the whole label for a screen reader — "Step 1,
	// Detect" — because the number on its own is a digit floating in front of
	// a word.
	const label = `${t("nav.stage.n", { n: String(step) })} · ${title}`;
	if (rail)
		return (
			<StageFlyout label={label} icon={icon} current={current}>
				<Rail.Provider value={false}>{children}</Rail.Provider>
			</StageFlyout>
		);
	const heading = (
		<>
			<span className="step num" aria-hidden>
				{step}
			</span>
			{title}
		</>
	);
	return (
		<div className={`nav-group${open ? "" : " is-closed"}${current ? " is-current" : ""}`}>
			<h2 aria-label={onToggle ? undefined : label}>
				{onToggle ? (
					<Button
						variant="ghost"
						className="stage-toggle w-full justify-start px-0 has-[>svg]:px-0"
						type="button"
						aria-expanded={open}
						aria-label={label}
						onClick={onToggle}
					>
						{heading}
						<ChevronRight className="stage-caret" aria-hidden />
					</Button>
				) : (
					heading
				)}
			</h2>
			{open ? children : null}
		</div>
	);
}

/**
 * The stage a path belongs to, or 0 for the screens outside the numbers — the
 * overview, and the content tree with the composer and the editor over it.
 */
function stageOf(pathname: string, base: string): number {
	if (!base || !pathname.startsWith(`${base}/`)) return 0;
	const head = pathname.slice(base.length + 1).split("/")[0];
	return LOOP.find((one) => one.path === head)?.step ?? 0;
}

/**
 * What is waiting on somebody in the content tree — gaps nobody has answered
 * and drafts nobody has approved — from the overview's own request.
 *
 * The same query key as the overview, so the column and the screen it opens on
 * share one answer rather than asking twice; a zero is not drawn, because a
 * badge on an empty queue is a number asking for attention it has not earned.
 */
function useWaitingCount(entityId: string, filters: FilterState): string | undefined {
	const { locale } = useI18n();
	const scope = { range: filters.range, engine: filters.engine, locale: filters.locale };
	const state = useQuery({
		queryKey: [...keys.entity(entityId), "overview", scope],
		queryFn: () => api.overview(entityId, filters),
		...read,
	});
	const loop = state.data?.loop_state;
	const waiting = loop ? loop.open_gaps + loop.waiting : 0;
	return waiting > 0 ? new Intl.NumberFormat(locale).format(waiting) : undefined;
}

/**
 * The Analytics module's half of the sidebar.
 *
 * The loop, from the one table that has it. Every reading is a row here rather
 * than a tab on its stage's screen: the sidebar is already the diagram of the
 * product, and a second navigation per stage would be five more places to look
 * for the same thing. They share a path and a scope bar, so moving between them
 * keeps the period, the engine and the topics somebody set.
 *
 * The content tree sits above the numbers: every stage adds to it, so it
 * belongs to none of them.
 */
export function AnalyticsNavigation({
	entityId,
	filters,
	rail,
	folds = true,
}: {
	entityId: string;
	filters: FilterState;
	/** Defaults to the frame's answer; passed only where there is no frame. */
	rail?: boolean;
	folds?: boolean;
}) {
	const t = useT();
	const base = brand(entityId);
	const { advanced } = useAccess();
	const chrome = useSidebarChrome();
	const isRail = rail ?? (chrome.isCollapsed && !chrome.isPeeking);
	const { pathname } = useLocation();
	const here = stageOf(pathname, base);
	const [open, setOpen] = useState(here || 1);
	const waiting = useWaitingCount(entityId, filters);

	// Landing on a screen opens the stage that owns it: nothing a person
	// navigated to is ever behind a closed heading.
	useEffect(() => {
		if (here) setOpen(here);
	}, [here]);

	const fold = (step: number) =>
		folds
			? { open: open === step, current: here === step, onToggle: () => setOpen(open === step ? 0 : step) }
			: { current: here === step };

	return (
		<Rail.Provider value={isRail}>
			<div className="nav-group">
				<Item to={`${base}/${CONTENT_PATH}`} icon={CONTENT_ICON} label={t("nav.content")} badge={waiting} />
			</div>
			{LOOP.map((stage) => (
				<Stage
					key={stage.key}
					step={stage.step}
					icon={READING_ICONS[stage.readings[0].slug]}
					title={t(`nav.stage.${stage.key}`)}
					{...fold(stage.step)}
				>
					{stage.readings
						.filter((reading) => advanced || !ADVANCED_READINGS.has(reading.slug))
						.map((reading) => (
							<Item
								key={reading.slug}
								to={`${base}/${stage.path}/${reading.slug}`}
								icon={READING_ICONS[reading.slug]}
								label={t(`reading.${reading.slug}`)}
							/>
						))}
				</Stage>
			))}
		</Rail.Provider>
	);
}
